/**
 * V2E Evidence Taxonomy — frontend shim.
 *
 * Consumes /api/console/evidence-taxonomy (single source of truth kept on
 * the backend in /app/backend/evidence_taxonomy.py). Falls back to a static
 * copy if the fetch fails so the UI still renders offline.
 *
 * Also exposes helpers for rendering the credibility chip on any signal —
 * every signal returned by the backend now carries `evidence_grade_normalized`
 * so components just call `renderGradeChip(signal)`.
 */
import axios from "axios";
import React from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Static fallback (mirror of backend evidence_taxonomy.py) ───────────

export const PEER_REVIEWED_GRADE = {
  high_certainty:      { label: "High certainty",     short: "High",
    definition: "Systematic review, meta-analysis, or RCT — very confident the estimate lies close to the true effect.",
    bg: "#E8F0EA", fg: "#2C5C42", border: "#B6D2BC", rank: 4 },
  moderate_certainty:  { label: "Moderate certainty", short: "Moderate",
    definition: "Prospective observational / cohort / registry study — moderately confident.",
    bg: "#E4EEF7", fg: "#22456A", border: "#B7CFE3", rank: 3 },
  low_certainty:       { label: "Low certainty",      short: "Low",
    definition: "Retrospective / case-control / cross-sectional / claims study — limited confidence.",
    bg: "#FDF1E6", fg: "#8C3A12", border: "#E8C19A", rank: 2 },
  very_low_certainty:  { label: "Very low certainty", short: "Very low",
    definition: "Narrative review, case series, or expert consensus — very limited confidence.",
    bg: "#F1ECF4", fg: "#5B3C7A", border: "#CCBADB", rank: 1 },
};

export const L1_CREDIBILITY = {
  platform_verified:            { label: "Platform-verified community source", short: "Platform-verified",
    definition: "Moderated advocacy forum, verified patient community, or named advocacy-organisation publication.",
    bg: "#E8F0EA", fg: "#2C5C42", border: "#B6D2BC", rank: 4 },
  peer_reviewed_qualitative:    { label: "Peer-reviewed qualitative study", short: "Peer-reviewed qualitative",
    definition: "Published qualitative research — COREQ-aligned interviews or thematic synthesis.",
    bg: "#E4EEF7", fg: "#22456A", border: "#B7CFE3", rank: 3 },
  aggregated_social_listening:  { label: "Aggregated social listening", short: "Aggregated SML",
    definition: "Aggregate volume + sentiment across hundreds to thousands of public posts.",
    bg: "#FDF1E6", fg: "#8C3A12", border: "#E8C19A", rank: 2 },
  unverified_anecdotal:         { label: "Unverified anecdotal post", short: "Unverified anecdotal",
    definition: "Single unverified user post from a public forum — illustrative only.",
    bg: "#F5F1EA", fg: "#7A6244", border: "#D9CFB8", rank: 1 },
};

// ─── Fetch dynamic taxonomy (cached module-level) ───────────────────────

let _taxonomyCache = null;
export async function fetchEvidenceTaxonomy() {
  if (_taxonomyCache) return _taxonomyCache;
  try {
    const { data } = await axios.get(`${API}/console/evidence-taxonomy`);
    _taxonomyCache = data;
    return data;
  } catch {
    // Fall through to static fallback (rendered as flat lists).
    return {
      peer_reviewed_grade: Object.entries(PEER_REVIEWED_GRADE)
        .sort((a, b) => b[1].rank - a[1].rank)
        .map(([key, v]) => ({ key, ...v, color_bg: v.bg, color_fg: v.fg, color_border: v.border })),
      l1_credibility: Object.entries(L1_CREDIBILITY)
        .sort((a, b) => b[1].rank - a[1].rank)
        .map(([key, v]) => ({ key, ...v, color_bg: v.bg, color_fg: v.fg, color_border: v.border })),
    };
  }
}

// ─── Grade lookup helper (works on the `evidence_grade_normalized` field) ─

export function lookupGrade(normalized) {
  if (!normalized || !normalized.key) return null;
  if (normalized.track === "peer_reviewed") return PEER_REVIEWED_GRADE[normalized.key] || null;
  if (normalized.track === "l1_credibility") return L1_CREDIBILITY[normalized.key] || null;
  return null;
}

// ─── Chip renderer ─────────────────────────────────────────────────────

export function EvidenceGradeChip({ signal, size = "sm", showTrackHint = false, testid }) {
  const norm = signal?.evidence_grade_normalized;
  if (!norm || !norm.key) return null;
  const meta = lookupGrade(norm);
  if (!meta) {
    // Unknown key — render a neutral chip with the raw label so the reader
    // still sees SOMETHING; better than silently dropping the field.
    return (
      <span
        className={size === "xs" ? "font-mono text-[9.5px] uppercase tracking-[0.14em] border px-1.5 py-0.5" :
                   "font-mono text-[10.5px] uppercase tracking-[0.14em] border px-2 py-0.5"}
        style={{ backgroundColor: "#F5F1EA", color: "#7A6244", borderColor: "#D9CFB8" }}
        title={norm.raw ? `Legacy label: ${norm.raw}` : ""}
        data-testid={testid}
      >
        {norm.label || norm.raw || "Ungraded"}
      </span>
    );
  }
  const trackHint = norm.track === "l1_credibility" ? "L1 qualitative" : "GRADE";
  const sizeClass = size === "xs"
    ? "font-mono text-[9.5px] uppercase tracking-[0.14em] border px-1.5 py-0.5"
    : "font-mono text-[10.5px] uppercase tracking-[0.14em] border px-2 py-0.5";
  return (
    <span
      className={sizeClass}
      style={{ backgroundColor: meta.bg, color: meta.fg, borderColor: meta.border }}
      title={`${meta.label} — ${meta.definition}${showTrackHint ? ` (${trackHint})` : ""}`}
      data-testid={testid}
    >
      {meta.label}
    </span>
  );
}

// ─── Legend component (for tab headers + PDF footer) ───────────────────

export function EvidenceLegend({ compact = false, testid = "evidence-legend" }) {
  const [taxonomy, setTaxonomy] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetchEvidenceTaxonomy().then(t => { if (!cancelled) setTaxonomy(t); });
    return () => { cancelled = true; };
  }, []);
  if (!taxonomy) return null;

  const renderRow = (item) => (
    <div key={item.key} className="flex items-start gap-2 py-0.5">
      <span
        className="inline-block font-mono text-[9px] uppercase tracking-[0.14em] border px-1.5 py-0.5 mt-[1px] shrink-0"
        style={{ backgroundColor: item.color_bg, color: item.color_fg, borderColor: item.color_border }}
      >
        {item.label}
      </span>
      {!compact && (
        <span className="text-[10.5px] text-[#5C6470] leading-snug">{item.definition}</span>
      )}
    </div>
  );

  return (
    <div className="border border-[#E5DFCF] bg-[#FAF7F1] px-4 py-3 rounded-sm" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#C84C21] mb-2">
        Evidence credibility · how to read this
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-[0.18em] text-[#A09680] mb-1.5">
            GRADE · peer-reviewed streams (L2 · L4 · L5)
          </div>
          {(taxonomy.peer_reviewed_grade || []).map(renderRow)}
        </div>
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-[0.18em] text-[#A09680] mb-1.5">
            L1 Digital &amp; Social Listening credibility
          </div>
          {(taxonomy.l1_credibility || []).map(renderRow)}
        </div>
      </div>
      {!compact && (
        <div className="mt-3 text-[10px] text-[#7A6244] italic leading-snug border-t border-[#E5DFCF] pt-2">
          Two-track by design. GRADE (NICE · WHO · Cochrane · ISPOR-HTA) grades the
          certainty of quantitative peer-reviewed evidence. Social-listening posts
          get a separate qualitative-credibility label — applying RCT-era grading
          to a forum post is a category error.
        </div>
      )}
    </div>
  );
}
